/**
 * The audit log's categories — the vocabulary, with nothing attached to it.
 *
 * ITS OWN MODULE FOR A BUNDLE REASON, and the reason is worth stating because the file otherwise
 * looks like it was split for no purpose.
 *
 * The rest of the audit vocabulary — `AUDIT_EVENTS` and its sixty labels, the category hints,
 * the payload formatter — lives in `./audit.ts` and is ~2 KB gzip. It is read by one report,
 * which loads on demand. But the report's ROUTE has to validate `?categories=` in
 * `validateSearch`, and `validateSearch` runs during routing: TanStack's `autoCodeSplitting`
 * splits out a route's `component` and leaves everything else in the eager route tree. So a
 * category list that lives beside the labels drags the labels onto every page of the app —
 * measured at 2.4 KB against a 180 KB budget that stood at 178.7 (DESIGN_SYSTEM.md §8).
 *
 * This is the same shape as the note in that document about `resolvePersonRef` pulling zod into
 * the eager chunk, and the same fix it recommends: the thing a route must know to ROUTE is
 * separated from the thing it needs to RENDER.
 *
 * `./audit.ts` re-exports both of these, so nothing importing the audit vocabulary as a whole has
 * to know this file exists.
 */

export const AUDIT_CATEGORIES = [
  'money',
  'bank',
  'reconciliation',
  'records',
  'access',
  'support',
  'settings',
  /**
   * Documents produced for somebody outside the agency, and their dispatch — M3's delivery half.
   *
   * ITS OWN CATEGORY RATHER THAN `records` OR `settings`, because the question it answers is one
   * an agency gets asked from outside: "you say you sent me my August statement — when, and to
   * what address". Filed under `records` it would sit among property-book edits, and the one
   * filter that makes that question answerable in one click would not exist.
   */
  'reporting',
  /**
   * An event this build has never been taught — see `KNOWN_AUDIT_EVENTS`.
   *
   * A real category and not a fallback for bad input. Events are added by whoever adds the
   * feature, so the registry always lags them; `other` is what stops a filtered report from
   * silently omitting the newest thing the system does.
   */
  'other',
] as const;

export type AuditCategory = (typeof AUDIT_CATEGORIES)[number];

export function isAuditCategory(value: string): value is AuditCategory {
  return (AUDIT_CATEGORIES as readonly string[]).includes(value);
}
